<div class="w-full">
  <!-- Search Bar & Status Filter -->
  <div class="glass-card p-4 rounded-2xl mb-6 relative z-100">
    <div class="flex flex-col md:flex-row gap-4 items-start md:items-center">
      <!-- Search Input -->
      <div class="relative flex-1 max-w-md">
        <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <span class="material-icons text-xl text-text-muted">search</span>
        </div>
        <input
          type="text"
          [(ngModel)]="searchTerm"
          (input)="onSearchChange()"
          placeholder="Suche nach Lieferzettel-Nr., Auftrag..."
          class="search-input w-full pl-12 pr-10 py-3 rounded-xl bg-white/8 border border-white/12 text-text-primary placeholder-text-muted focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
        />
        @if (searchTerm) {
          <button
            type="button"
            class="absolute inset-y-0 right-0 pr-4 flex items-center text-text-muted hover:text-text-primary transition-colors"
            (click)="clearSearch()"
          >
            <span class="material-icons text-xl">close</span>
          </button>
        }
      </div>

      <!-- Status Filter -->
      <div class="flex items-center gap-2">
        <span class="material-icons text-text-muted">filter_list</span>
        <p-multiSelect
          [(ngModel)]="statusFilter"
          [options]="statusOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Status filtern"
          [filter]="false"
          [showClear]="true"
          [display]="'chip'"
          [maxSelectedLabels]="4"
          styleClass="multiselect-component-style"
          panelStyleClass="multiselect-component-panel-style"
          (onChange)="onStatusFilterChange()"
        >
          <ng-template pTemplate="item" let-option>
            <div class="option-row">
              <span class="option-label">{{ option.label }}</span>
            </div>
          </ng-template>
        </p-multiSelect>
      </div>

      <div class="md:ml-auto">
        <button type="button" class="btn-primary flex items-center gap-2" (click)="openCreatePage()">
          <span class="material-icons text-xl">add</span>
          Lieferzettel erstellen
        </button>
      </div>
    </div>
  </div>

  <!-- Delivery Notes Table -->
  <div class="glass-card rounded-2xl overflow-hidden">
    <!-- Loading State -->
    @if (isLoading) {
      <div class="flex items-center justify-center py-20">
        <div class="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    } @else {
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b border-white/10">
              <th class="table-header" (click)="onSort('noteNumber')">
                <div class="flex items-center gap-1 cursor-pointer hover:text-text-primary transition-colors">
                  Lieferzettel-Nr.
                  <span class="material-icons text-lg">{{ getSortIcon('noteNumber') }}</span>
                </div>
              </th>
              <th class="table-header" (click)="onSort('order.internalOrderId')">
                <div class="flex items-center gap-1 cursor-pointer hover:text-text-primary transition-colors">
                  Auftrag-Nr.
                  <span class="material-icons text-lg">{{ getSortIcon('order.internalOrderId') }}</span>
                </div>
              </th>
              <th class="table-header" (click)="onSort('order.companyName')">
                <div class="flex items-center gap-1 cursor-pointer hover:text-text-primary transition-colors">
                  Kunde
                  <span class="material-icons text-lg">{{ getSortIcon('order.companyName') }}</span>
                </div>
              </th>
              <th class="table-header">
                <div class="flex items-center gap-1">Positionen</div>
              </th>
              <th class="table-header" (click)="onSort('status')">
                <div class="flex items-center gap-1 cursor-pointer hover:text-text-primary transition-colors">
                  Status
                  <span class="material-icons text-lg">{{ getSortIcon('status') }}</span>
                </div>
              </th>
              <th class="table-header" (click)="onSort('shippingType')">
                <div class="flex items-center gap-1 cursor-pointer hover:text-text-primary transition-colors">
                  Versandart
                  <span class="material-icons text-lg">{{ getSortIcon('shippingType') }}</span>
                </div>
              </th>
              <th class="table-header" (click)="onSort('employeeId')">
                <div class="flex items-center gap-1 cursor-pointer hover:text-text-primary transition-colors">
                  MA
                  <span class="material-icons text-lg">{{ getSortIcon('employeeId') }}</span>
                </div>
              </th>
              <th class="table-header" (click)="onSort('createdAt')">
                <div class="flex items-center gap-1 cursor-pointer hover:text-text-primary transition-colors">
                  Erstellt am
                  <span class="material-icons text-lg">{{ getSortIcon('createdAt') }}</span>
                </div>
              </th>

              <th class="table-header">
                <span class="sr-only">Aktionen</span>
              </th>
            </tr>
          </thead>
          <tbody>
            @if (deliveryNotes.length === 0) {
              <tr>
                <td colspan="9" class="px-6 py-12 text-center text-text-muted">Keine Lieferzettel gefunden...</td>
              </tr>
            } @else {
              @for (note of deliveryNotes; track note._id) {
                <tr class="table-row border-b border-white/6 last:border-b-0 cursor-pointer hover:bg-white/4" (click)="openOverviewModal(note)">
                  <td class="table-cell font-medium text-text-primary">{{ note.noteNumber }}</td>
                  <td class="table-cell">
                    <span class="text-xs bg-white/8 px-2 py-1 rounded">{{ note.order.internalOrderId }}</span>
                  </td>
                  <td class="table-cell text-text-secondary">{{ note.order.companyName }}</td>
                  <td class="table-cell">
                    <span class="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-400/15 text-blue-400">
                      {{ getPositionsCount(note) }} Position(en)
                    </span>
                  </td>
                  <td class="table-cell">
                    <div class="flex items-center gap-2">
                      <span class="inline-block px-2.5 py-1 rounded-full text-xs font-semibold" [ngClass]="getStatusClass(note.status)">
                        {{ getStatusName(note.status) }}
                      </span>
                      @if (!!note.exportDate) {
                        <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-400/15 text-orange-400">
                          <span class="material-icons text-sm">lock</span>
                        </span>
                      }
                      @if (getNextStatus(note.status) && !note.exportDate) {
                        <button
                          type="button"
                          class="p-1 rounded-lg flex text-text-muted hover:text-emerald-400 hover:bg-emerald-400/10 transition-all"
                          (click)="onQuickStatusChange(note, getNextStatus(note.status)!); $event.stopPropagation()"
                          [title]="'Zu ' + getStatusName(getNextStatus(note.status)!) + ' ändern'"
                        >
                          <span class="material-icons text-lg">arrow_forward</span>
                        </button>
                      }
                    </div>
                  </td>
                  <td class="table-cell">
                    <div class="flex items-center gap-2">
                      <span
                        class="inline-block px-2.5 py-1 rounded-full text-xs font-semibold"
                        [ngClass]="isPaletteShippingWarning(note) ? 'bg-red-500/20 text-red-300' : 'bg-white/8 text-text-primary'"
                      >
                        {{ getShippingTypeName(note.shippingType) }}
                      </span>
                      @if (isPaletteShippingWarning(note)) {
                        <span class="material-icons text-red-400 text-lg leading-none" title="Palettenversand noch nicht ausgeliefert">warning</span>
                      }
                    </div>
                  </td>
                  <td class="table-cell">
                    <span class="inline-block px-2.5 py-1 rounded-full text-xs font-semibold">
                      {{ note.employeeId }}
                    </span>
                  </td>
                  <td class="table-cell">
                    <span class="text-xs bg-white/8 px-2 py-1 rounded">{{ formatDate(note.createdAt) }}</span>
                  </td>

                  <td class="table-cell">
                    <div class="flex items-center gap-1">
                      <button
                        type="button"
                        class="p-2 rounded-lg text-text-muted hover:text-primary flex items-center justify-center hover:bg-white/10 transition-all"
                        (click)="openOverviewModal(note); $event.stopPropagation()"
                        title="Ansehen"
                      >
                        <span class="material-icons text-lg">visibility</span>
                      </button>
                      <button
                        type="button"
                        class="p-2 rounded-lg text-text-muted flex items-center justify-center transition-all"
                        [ngClass]="note.exportDate ? 'opacity-40 cursor-not-allowed' : 'hover:text-primary hover:bg-white/10'"
                        [disabled]="note.exportDate"
                        (click)="openEditPage(note); $event.stopPropagation()"
                        title="Bearbeiten"
                      >
                        <span class="material-icons text-lg">edit</span>
                      </button>
                      <button
                        type="button"
                        class="p-2 rounded-lg text-text-muted flex items-center justify-center transition-all"
                        [ngClass]="note.exportDate ? 'opacity-40 cursor-not-allowed' : 'hover:text-red-400 hover:bg-red-400/10'"
                        [disabled]="note.exportDate"
                        (click)="onDeleteNote(note); $event.stopPropagation()"
                        title="Löschen"
                      >
                        <span class="material-icons text-lg">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      @if (totalItems > 0) {
        <div class="px-6 py-4 border-t border-white/10">
          <app-pagination
            [currentPage]="currentPage"
            [totalPages]="totalPages"
            [totalItems]="totalItems"
            [pageSize]="pageSize"
            (pageChange)="onPageChange($event)"
          ></app-pagination>
        </div>
      }
    }
  </div>
</div>

<!-- Overview Modal -->
@if (showOverviewModal) {
  <app-delivery-note-overview-modal
    [deliveryNote]="selectedDeliveryNote"
    [orderInfo]="selectedOrderInfo"
    (close)="closeOverviewModal()"
    (edit)="onEditFromModal($event)"
    (noteUpdated)="onNoteUpdated($event)"
  ></app-delivery-note-overview-modal>
}
